<script>
export default {
  name: "AppSelect",

  props: {
    value: [String, Number, Object],
    items: { type: Array, default: () => [] },
    itemText: { type: String, default: "text" },
    itemValue: { type: String, default: "value" },
    placeholder: { type: String, default: "" },
    label: { type: String, default: "" },
    rules: { type: Array, default: () => [] },
    required: Boolean,
    disabled: Boolean,
    dense: Boolean,
    hideDetails: Boolean,
    error: Boolean,
    errorMessages: { type: [String, Array], default: () => [] },
  },

  data: () => ({
    open: false,
    hasError: false,
    errorText: "",
  }),

  computed: {
    selectedLabel() {
      if (this.value == null) return "";
      const item = this.items.find((i) => {
        if (typeof i === "object") return i[this.itemValue] === this.value;
        return i === this.value;
      });
      if (!item) return "";
      return typeof item === "object" ? item[this.itemText] : item;
    },
  },

  methods: {
    toggle() {
      if (this.disabled) return;
      this.open = !this.open;
    },

    select(item) {
      const val = typeof item === "object" ? item[this.itemValue] : item;
      this.$emit("input", val);
      this.open = false;
      this.validate(val);
    },

    getItemLabel(item) {
      return typeof item === "object" ? item[this.itemText] : item;
    },

    getItemValue(item) {
      return typeof item === "object" ? item[this.itemValue] : item;
    },

    close(e) {
      if (!this.$el.contains(e.target)) {
        this.open = false;
      }
    },

    validate(val) {
      const v = val !== undefined ? val : this.value;
      for (const rule of this.rules) {
        const result = rule(v);
        if (result !== true) {
          this.hasError = true;
          this.errorText = result;
          return false;
        }
      }
      this.hasError = false;
      this.errorText = "";
      return true;
    },

    // Public method for v-form validation
    reset() {
      this.hasError = false;
      this.errorText = "";
    },
  },

  mounted() {
    document.addEventListener("click", this.close);
  },

  beforeDestroy() {
    document.removeEventListener("click", this.close);
  },
};
</script>

<template>
  <div class="app-select" :class="{ 'app-select--open': open, 'app-select--error': hasError || error, 'app-select--disabled': disabled, 'app-select--dense': dense }">
    <label v-if="label" class="app-select__label">{{ label }}</label>

    <div class="app-select__control" @click="toggle">
      <span v-if="selectedLabel" class="app-select__value">{{ selectedLabel }}</span>
      <span v-else class="app-select__placeholder">{{ placeholder }}</span>
      <svg class="app-select__chevron" :class="{ 'app-select__chevron--up': open }" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>

    <transition name="app-select-drop">
      <div v-if="open" class="app-select__dropdown">
        <div
          v-for="item in items"
          :key="getItemValue(item)"
          class="app-select__option"
          :class="{ 'app-select__option--active': getItemValue(item) === value }"
          @click.stop="select(item)"
        >
          {{ getItemLabel(item) }}
        </div>
      </div>
    </transition>

    <div v-if="!hideDetails && (hasError || error)" class="app-select__error">
      {{ errorText || (Array.isArray(errorMessages) ? errorMessages[0] : errorMessages) }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.app-select {
  position: relative;
  width: 100%;
}

.app-select__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  margin-bottom: 6px;
}

.app-select__control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  padding: 0 12px;
  border: 1.5px solid #d0d5dd;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  user-select: none;
}
.app-select--dense .app-select__control {
  min-height: 40px;
}

.app-select__control:hover {
  border-color: #98a2b3;
}
.app-select--open .app-select__control {
  border-color: #00274c;
  box-shadow: 0 0 0 1px #00274c;
}
.app-select--error .app-select__control {
  border-color: #ff5252;
}

.app-select__value {
  font-size: 14px;
  color: #111318;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-select__placeholder {
  font-size: 14px;
  color: #9ca3af;
  flex: 1;
}

.app-select__chevron {
  color: #6b7280;
  flex-shrink: 0;
  transition: transform 0.2s;
}
.app-select__chevron--up {
  transform: rotate(180deg);
}

.app-select__dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 100;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.06);
  padding: 4px;
  max-height: 220px;
  overflow-y: auto;
}

.app-select__option {
  padding: 8px 12px;
  font-size: 14px;
  color: #374151;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.1s;
}
.app-select__option:hover {
  background: #f3f4f6;
}
.app-select__option--active {
  background: rgba(0, 39, 76, 0.08);
  color: #00274c;
  font-weight: 500;
}

.app-select__error {
  font-size: 12px;
  color: #ff5252;
  margin-top: 4px;
  padding-left: 2px;
}

.app-select--disabled .app-select__control {
  opacity: 0.6;
  pointer-events: none;
}

/* Transition */
.app-select-drop-enter-active,
.app-select-drop-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}
.app-select-drop-enter,
.app-select-drop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ── Dark mode ── */
.theme--dark .app-select__control {
  background: #22232e;
  border-color: #2a2c3a;
  color: #d5d6de;
}
.theme--dark .app-select__control:hover {
  border-color: #33354a;
}
.theme--dark .app-select--open .app-select__control {
  border-color: #ffd451;
  box-shadow: 0 0 0 1px #ffd451;
}
.theme--dark .app-select__value {
  color: #d5d6de;
}
.theme--dark .app-select__placeholder {
  color: #606275;
}
.theme--dark .app-select__chevron {
  color: #606275;
}
.theme--dark .app-select__label {
  color: #8b8d9e;
}
.theme--dark .app-select__dropdown {
  background: #1a1b23;
  border-color: #2a2c3a;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}
.theme--dark .app-select__option {
  color: #d5d6de;
}
.theme--dark .app-select__option:hover {
  background: #22232e;
}
.theme--dark .app-select__option--active {
  background: rgba(255, 212, 81, 0.08);
  color: #ffd451;
}
</style>
